import superagent from 'superagent'
import Wind from './wind'
import Elevation from './elevation'

/**
 * Weather Center data (WEB-7144), all from endpoints mobile already uses:
 *
 *   GET Weather/Get?latitude&longitude                current observation
 *   GET Weather/Get?latitude&longitude&days=7         daily forecast (periods[])
 *   GET Weather/Get?latitude&longitude&hours=48       hourly forecast (periods[], UTC timestamps)
 *   GET Celestial/Get?latitude&longitude&date&days    sun and moon, one row per local day
 *   GET Wind/GetHourly, Wind/GetDaily                  per-stand wind (lib/api/wind)
 *   GET Elevation/Get                                  meters (lib/api/elevation)
 *
 * The map center is named with the Maps JavaScript API's Geocoder rather than ReverseGeocode/Get:
 * that endpoint keeps only Google's first result, which away from towns is a bare plus code with
 * no town, county or state.
 *
 * Results are cached in memory per rounded location, like mobile's WeatherCacheManager: weather
 * and elevation for 10 minutes, stand wind for 2 hours (hourly) and 6 hours (daily).
 */

const WEATHER_TTL = 10 * 60 * 1000
const CELESTIAL_TTL = 6 * 60 * 60 * 1000
const STAND_HOURLY_TTL = 2 * 60 * 60 * 1000
const STAND_DAILY_TTL = 6 * 60 * 60 * 1000
const PLACE_NAME_TTL = 24 * 60 * 60 * 1000

const cache = new Map()

// One request per key at a time; a failure is not cached.
const cached = (key, ttl, load, force) => {
  const hit = cache.get(key)
  if (hit && !force && Date.now() - hit.at < ttl) return hit.promise
  const promise = load()
  cache.set(key, { at: Date.now(), promise })
  promise.catch(() => {
    if (cache.get(key) && cache.get(key).promise === promise) cache.delete(key)
  })
  return promise
}

const place = (lat, lng) => `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`

const get = (path, query, token) => new Promise((resolve, reject) => {
  superagent.get(`${__API_URI__}/${path}`)
    .query(query)
    .set('Authorization', `Bearer ${token}`)
    .set('Content-Type', 'application/json')
    .set('Platform', 'Web')
    .set('Version', `${__version__}`)
    .end((err, res) => {
      if (err) return reject(err)
      resolve(res.body)
    })
})

const weatherCenterApi = {
  observation: ({ lat, lng, token, force }) =>
    cached(`obs:${place(lat, lng)}`, WEATHER_TTL, () => get('Weather/Get', { latitude: lat, longitude: lng }, token), force),

  daily: ({ lat, lng, token, force }) =>
    cached(`daily:${place(lat, lng)}`, WEATHER_TTL, () => get('Weather/Get', { latitude: lat, longitude: lng, days: 7 }, token), force),

  hourly: ({ lat, lng, token, force }) =>
    cached(`hourly:${place(lat, lng)}`, WEATHER_TTL, () => get('Weather/Get', { latitude: lat, longitude: lng, hours: 48 }, token), force),

  // `date` is the location's local date, "YYYY-MM-DD".
  celestial: ({ lat, lng, date, days, token }) =>
    cached(`celestial:${place(lat, lng)}:${date}:${days}`, CELESTIAL_TTL,
      () => get('Celestial/Get', { latitude: lat, longitude: lng, date, days }, token)),

  // Google's results for a point (billed per lookup, hence the long cache).
  reverseGeocode: ({ lat, lng }) =>
    cached(`place:${place(lat, lng)}`, PLACE_NAME_TTL, () => {
      if (!window.google || !google.maps || !google.maps.Geocoder) return Promise.reject(new Error('Geocoder not loaded'))
      return new google.maps.Geocoder().geocode({ location: { lat, lng } }).then(response => response.results || [])
    }),

  elevation: ({ lat, lng, token }) =>
    cached(`elevation:${place(lat, lng)}`, WEATHER_TTL, () => Elevation.getElevation(lat, lng, token)),

  standHourly: ({ stand, token }) =>
    cached(`standHourly:${stand.id}:${place(stand.latitude, stand.longitude)}`, STAND_HOURLY_TTL,
      () => Wind.fetchHourly({ lat: stand.latitude, lng: stand.longitude, token })),

  standDaily: ({ stand, token }) =>
    cached(`standDaily:${stand.id}:${place(stand.latitude, stand.longitude)}`, STAND_DAILY_TTL,
      () => Wind.fetchDaily({ lat: stand.latitude, lng: stand.longitude, token }))
}

export default weatherCenterApi
